import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Phone, ClipboardList, CheckCircle, ArrowRight, Droplets } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ServiceGallery } from '../components/ServiceGallery';

export const GutterInstallationPage = () => {
  useEffect(() => {
    // SEO: Set page title and meta description (Priority 8)
    document.title = "Gutter Installation Sydney | Gutter Replacement and Downpipes | Dezire Metal Roofing";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Dezire Metal Roofing provides gutter installation, gutter replacement, and drainage solutions across Greater Sydney. Get a free quote today.');
    }
    window.scrollTo(0, 0);
  }, []);

  const gutterServices = [
    "New gutter installation",
    "Gutter replacement",
    "Downpipe installation",
    "Drainage improvements",
    "Gutter guard solutions"
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="page-title">
              Gutter Installation and Replacement <span className="text-primary">Sydney</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Good guttering is essential for protecting your home from water damage. Dezire Metal Roofing provides gutter installation and replacement services across Greater Sydney, helping homeowners improve drainage, reduce overflow, and protect roofing and exterior areas.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/book-inspection">
                <Button size="lg" className="bg-primary hover:bg-accent text-white" data-testid="hero-cta-btn">
                  <ClipboardList className="w-5 h-5 mr-2" />
                  Get a Free Quote
                </Button>
              </Link>
              <a href="/call">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-gray-900">
                  <Phone className="w-5 h-5 mr-2" />
                  Call 0433 973 844
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left Column */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Our Guttering Services
              </h2>
              <ul className="space-y-4 mb-8">
                {gutterServices.map((service, index) => (
                  <li key={`gutter-service-${index}-${service.slice(0, 10)}`} className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-primary mr-3 flex-shrink-0" />
                    <span className="text-gray-700 text-lg">{service}</span>
                  </li>
                ))}
              </ul>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
                <div className="flex items-start">
                  <Droplets className="w-6 h-6 text-blue-500 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">Why Guttering Matters</h3>
                    <p className="text-gray-700">
                      Damaged or ineffective gutters can lead to water overflow, fascia damage, staining, and long-term property issues. Well-installed guttering helps direct water away from your home properly.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div>
              <div className="bg-gray-50 p-8 rounded-xl">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Get a Free Quote
                </h2>
                <p className="text-gray-600 mb-6">
                  Contact us today for a free quote on gutter installation or replacement in Greater Sydney.
                </p>
                <p className="text-gray-600 mb-8">
                  We&apos;ll assess your property, recommend the best solution, and provide a detailed quote with no obligation.
                </p>

                <Link to="/book-inspection">
                  <Button className="w-full bg-primary hover:bg-accent text-white mb-4" data-testid="content-cta-btn">
                    Get a Free Quote
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                
                <p className="text-center text-gray-500 text-sm">
                  Or call us directly: <a href="/call" className="text-primary font-semibold">0433 973 844</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <ServiceGallery service="gutter-installation" />

      {/* Related Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Related Services
          </h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link to="/services/metal-roof-installation" className="bg-gray-50 p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <h3 className="font-bold text-gray-900 mb-2">Metal Roof Installation</h3>
              <p className="text-gray-600 text-sm">New roof installations</p>
            </Link>
            <Link to="/services/roof-repairs" className="bg-gray-50 p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <h3 className="font-bold text-gray-900 mb-2">Roof Repairs</h3>
              <p className="text-gray-600 text-sm">Fix leaks and damage</p>
            </Link>
            <Link to="/services/roof-replacement" className="bg-gray-50 p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <h3 className="font-bold text-gray-900 mb-2">Roof Replacement</h3>
              <p className="text-gray-600 text-sm">Full roof replacement</p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GutterInstallationPage;
